import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FiArrowRight,
  FiCheckCircle,
  FiEye,
  FiEyeOff,
  FiLock,
  FiShield,
  FiUser,
  FiZap,
} from 'react-icons/fi';
import API from '../api';
import { useAuth } from './AuthContext';
import { AuthBrand, AuthFinancialPreview } from './AuthShell';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const [form, setForm] = useState({ username: '', password: '' });
  const [error, setError] = useState(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const successMessage = location.state?.successMessage;

  const handleInput = (event) => {
    setForm({ ...form, [event.target.name]: event.target.value });
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    if (isLoggingIn) return;
    setIsLoggingIn(true);
    try {
      await API.post('/token/', { username: form.username, password: form.password });
      const userRes = await API.get('/user/');
      setUser(userRes.data);
      setError(null);
      navigate('/dashboard');
    } catch (err) {
      console.error(err);
      const responseData = err?.response?.data;
      const errorCode = responseData?.code || responseData?.detail?.code;
      const detailText = typeof responseData?.detail === 'string'
        ? responseData.detail
        : JSON.stringify(responseData?.detail || '');

      if (errorCode === 'account_closed' || detailText.includes('account_closed')) {
        setError(null);
        navigate('/compte-ferme', { replace: true });
      } else {
        setError('Identifiants invalides');
      }
    } finally {
      setIsLoggingIn(false);
    }
  };

  return (
    <main className="auth-container auth-container--login">
      <div className="auth-layout">
        <aside className="auth-showcase" aria-label="Présentation Adabo Cash">
          <div className="auth-showcase__orb auth-showcase__orb--one" aria-hidden="true" />
          <div className="auth-showcase__orb auth-showcase__orb--two" aria-hidden="true" />
          <div className="auth-showcase__content">
            <AuthBrand />
            <div className="auth-showcase__copy">
              <span className="auth-eyebrow"><FiZap /> La finance, simplifiée</span>
              <h1>Votre argent.<br />Votre avenir.</h1>
              <p>Une expérience simple et sécurisée pour gérer vos finances au quotidien.</p>
            </div>
            <AuthFinancialPreview />
            <div className="auth-benefits">
              <span><FiShield /> Sécurité renforcée</span>
              <span><FiCheckCircle /> Transactions instantanées</span>
            </div>
          </div>
        </aside>

        <section className="auth-card">
          <div className="auth-card__mobile-brand"><AuthBrand /></div>
          <header className="auth-card__header">
            <h2>Connectez-vous</h2>
            <p>Accédez à votre tableau de bord en toute sécurité.</p>
          </header>

          {successMessage && <div className="settings-message" role="status">{successMessage}</div>}
          {error && <div className="error-message" role="alert">{error}</div>}

          <form onSubmit={handleLogin}>
            <div className="auth-field">
              <label htmlFor="login-username">Nom d'utilisateur ou email</label>
              <div className="auth-input-wrap">
                <FiUser aria-hidden="true" />
                <input
                  id="login-username"
                  name="username"
                  type="text"
                  placeholder="Entrez votre identifiant"
                  value={form.username}
                  onChange={handleInput}
                  autoComplete="username"
                  required
                />
              </div>
            </div>

            <div className="auth-field">
              <div className="auth-field__label-row">
                <label htmlFor="login-password">Mot de passe</label>
                <button className="switch-btn" type="button" onClick={() => navigate('/reset-password')}>
                  Mot de passe oublié&nbsp;?
                </button>
              </div>
              <div className="password-field auth-input-wrap">
                <FiLock aria-hidden="true" />
                <input
                  id="login-password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Entrez votre mot de passe"
                  value={form.password}
                  onChange={handleInput}
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                  onClick={() => setShowPassword((value) => !value)}
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
            </div>

            <button type="submit" className={isLoggingIn ? 'is-submitting' : ''}>
              <span>{isLoggingIn ? 'Connexion...' : 'Se connecter'}</span>
              {!isLoggingIn && <FiArrowRight aria-hidden="true" />}
            </button>
          </form>

          <div className="auth-links">
            <span>Vous n'avez pas encore de compte&nbsp;?</span>
            <button className="switch-btn" type="button" onClick={() => navigate('/register')}>Créer un compte</button>
          </div>
        </section>
      </div>
    </main>
  );
}
